/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import {
  Button,
  ButtonGroup,
  Container,
  Col,
  FormControl,
  FormCheck,
  InputGroup,
  Table,
  Row,
  Spinner,
  OverlayTrigger,
  Tooltip,
  Form,
  Popover,
} from 'react-bootstrap';
import Select from 'react-select';
import jwtDecode from 'jwt-decode';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPeopleArrows,
  faRedo,
  faUserPlus,
  faTrash,
  faTimesCircle,
  faSearch,
  faSortAlphaDown,
  faSortAlphaUp,
  faPencilAlt,
  faUsersCog,
  faBars,
} from '@fortawesome/free-solid-svg-icons';

import { getUsersList, deleteUser, exportUsers, exportUsersTemplate } from '../../../../actions/user.action';
import Pagination from '../../../../Components/Pagination';
import { UserForm, UserBatch } from '..';
import types from '../../../../types';
import { ROLES } from '../../../../constants/common';
import { getOrgList, getOrgListByUser } from '../../../../actions/organization.actions';
import { getInstitutionList } from '../../../../actions/institution.actions';
import {
  assignOrgToUserApi,
  createUserApi,
  removeOrgToUserApi,
  updateUserApi,
  demoteOrgUserApi,
} from '../../../../api/users';
import RemoveFromOrganizationModal from './RemoveFromOrganizationModal';
import DeleteUserModal from './DeleteUserModal';
import MultiDeleteUserModal from './MultiDeleteUserModal';
import { getColumnsVisibleStorage, setColumnsVisibleStorage } from './columnsVisibleStorage';

const UserList = () => {
  const columnsVisibleStorage = getColumnsVisibleStorage();
  const dispatch = useDispatch();
  const [filter, setFilter] = useState({
    page: 1,
    size: 10,
    dir: 'desc',
    sort: 'lastLogin'
  });

  const [show, setShow] = useState(false);
  const [showMultiDelete, setShowMultiDelete] = useState(false);
  const [showDemote, setShowDemote] = useState(false);
  const [checkAll, setCheckAll] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState({});
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [saveError, setSaveError] = useState();
  const [columns, setColumns] = useState([]);
  const [columnsVisible, setColumnsVisible] = useState(columnsVisibleStorage);

  const {
    loading,
    users: userList,
    totalSize,
    exportFile,
    isProcessingExport,
    canGetList,
  } = useSelector((state) => state.users);
  const { organizations: orgList, loading: orgLoading } = useSelector(
    (state) => state.organizations
  );

  const { institutions } = useSelector(
    (state) => state.institutions
  );

  const storeJwt = useSelector((state) => state.session.jwt);
  const currentUser = jwtDecode(storeJwt);

  const handleClose = () => {
    setShow(false);
    setShowMultiDelete(false);
    setShowDemote(false);
    setEditItem(null);
    setDeleteItem({});
  };

  const handleCreateUser = () => {
    setEditItem({
      email: '',
      firstName: '',
      lastName: '',
      role: '',
      institution: '',
      organizations: [],
    })
    setShow(true);
    dispatch({ type: types.reducerTypes.USERS_SAVE_CLEAR });
  };

  const retrieveUsers = (newFilter) => {
    if (newFilter) {
      setFilter(newFilter);
    }
    dispatch(getUsersList(newFilter || filter, storeJwt));
  };

  const handleCheckAll = (e) => {
    setCheckAll(e.target.checked);
  };

  const handleCheckUser = (user) => {
    const userIndex = selectedUsers.findIndex((su) => su === user.id);
    if (userIndex === -1) {
      setSelectedUsers([...selectedUsers, user.id]);
    } else {
      setSelectedUsers(selectedUsers.filter((id) => user.id !== id));
    }
  };

  const handleSearch = () => {
    setEditItem(null);
    setSaveError(null);
    setShow(false);
    let newFilter = null;
    if (filter.page !== 1) {
      newFilter = {
        ...filter,
        page: 1
      };
    }
    retrieveUsers(newFilter);
  };

  useEffect(() => {
    if (currentUser.role === 'ORGANIZATION_ADMIN') {
      dispatch(getOrgListByUser(currentUser.userId, storeJwt));
    } else {
      dispatch(getOrgList(null, storeJwt));
    }
    dispatch(getInstitutionList(storeJwt));
    retrieveUsers();
  }, []);

  useEffect(() => {
    if (exportFile && exportFile !== '' && !isProcessingExport) {
      const link = document.createElement('a');
      link.download = true;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.href = exportFile;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [exportFile, isProcessingExport]);

  useEffect(() => {
    if (canGetList) {
      retrieveUsers();
    }
  }, [canGetList]);

  const handleClearFilters = () => {
    const newFilter = {
      page: filter.page,
      size: filter.size,
      dir: 'desc',
      sort: 'lastLogin',
    };
    retrieveUsers(newFilter);
  };

  const handleFilterKeyUp = (event) => {
    if (event.key === 'Enter') {
      retrieveUsers({
        ...filter,
        page: 1
      });
    }
  }

  const handleFilterChange = (e, autoSeact) => {
    const newFilter = {
      ...filter,
      [e.target.name]: e.target.value
    };
    if (autoSeact) {
      retrieveUsers(newFilter);
    } else {
      setFilter(newFilter);
    }
  }

  const handleSortChange = (newSortCol) => {
    const newFilter = {
      ...filter,
      sort: newSortCol,
      dir: 'asc',
    };
    if (filter.sort === newSortCol) {
      newFilter.dir = filter.dir === 'asc' ? 'desc' : 'asc'
    }
    retrieveUsers(newFilter);
  };

  const handlePageSizeChange = (e) => {
    const newFilter = {
      ...filter,
      page: 1,
      size: e.target.value,
    };
    retrieveUsers(newFilter);
  };

  const handleChangePage = (newPage) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const newFilter = {
      ...filter,
      page: newPage,
    };
    retrieveUsers(newFilter);
  };

  const handleUpdateUser = async (editUser) => {
    const currentOrgIds = editItem.organizations.map((org) => org.id);
    const newOrgs = editUser.organization.filter((oId) => !currentOrgIds.includes(oId));
    const orgsToRemove = currentOrgIds.filter((oId) => !editUser.organization.includes(oId));
    updateUserApi(
      {
        role: editUser.role,
        firstName: editUser.firstName,
        lastName: editUser.lastName,
        institution: editUser.institution,
      },
      storeJwt,
      editItem.id
    );
    if (newOrgs.length) {
      await Promise.all(
        newOrgs.map((orgId) =>
          assignOrgToUserApi(editItem.id, orgId, editUser.role === 'ORGANIZATION_ADMIN', storeJwt)
        )
      );
    }
    if (orgsToRemove.length) {
      await Promise.all(
        orgsToRemove.map((orgId) => removeOrgToUserApi(editItem.id, orgId, storeJwt))
      );
    }
    retrieveUsers();
  };

  const handleSave = async (newUser) => {
    if (editItem.id) {
      handleUpdateUser(newUser);
    } else {
      try {
        const userCreated = await createUserApi(newUser, storeJwt);
        await Promise.all(
          newUser.organization.map((org) =>
            assignOrgToUserApi(
              userCreated.data.id,
              org,
              newUser.role === 'ORGANIZATION_ADMIN',
              storeJwt
            )
          )
        );
        setSaveError(null);
        setShow(false);
      } catch (e) {
        setSaveError(e?.response?.data?.message || 'Unexpected Error!');
      }
    }
    handleSearch();
  };

  const handleDeleteUser = (ids) => {
    dispatch(deleteUser(ids, storeJwt));
    setShowMultiDelete(false);
  };

  const handleDemoteUser = async () => {
    await Promise.all(selectedUsers.map((id) => demoteOrgUserApi(id, storeJwt)));
    setShowDemote(false);
    retrieveUsers();
  };

  const handleDemoteSingleUser = async (id) => {
    await demoteOrgUserApi(id, storeJwt);
    retrieveUsers();
  };

  const availableRoles = useMemo(() => {
    if (currentUser.role === 'SUPER_ADMIN') {
      return ROLES;
    }
    if (currentUser.role === 'ORGANIZATION_ADMIN') {
      return ['ORGANIZATION_ADMIN', 'NATIONAL_USER'];
    }
    return ['ADMIN', 'NATIONAL_USER'];
  }, [currentUser]);

  const handleDelete = () => {
    if (currentUser.role === 'ORGANIZATION_ADMIN') {
      handleDemoteSingleUser(deleteItem.id);
    } else {
      handleDeleteUser([deleteItem.id]);
    }
    setDeleteItem({});
  };

  const handleDeleteMultiple = () => {
    handleDeleteUser(selectedUsers);
  };

  const handleExportUsers = () => {
    dispatch(exportUsers(storeJwt, selectedUsers));
  };

  const handleExportUsersTemplate = () => {
    dispatch(exportUsersTemplate(storeJwt));
  };
  
  const filterOrgs = (org) => {
    return currentUser.role === 'SUPER_ADMIN' ||
      currentUser.role === 'ADMIN' ||
      orgList.findIndex((o) => o.id === org.id) >= 0
  };

  const usersSelectedHaveMoreOrgs = () => {
    let result = false;
    const users = userList.filter((user) => selectedUsers.includes(user.id));
    users.forEach((user) => {
      if (user.organizations) {
        const filteredUserOrgs = user.organizations.filter(filterOrgs);
        if (filteredUserOrgs.length > 1) {
          result = true;
        }
      }
    });
    return result;
  };

  const showAndHideColumn = (column) => {
    let visibles = { ...columnsVisible };
    visibles[column] = !visibles[column];
    setColumnsVisible(visibles);
    setColumnsVisibleStorage(visibles);
  }

  const loadColumns = useCallback(() => {
    setColumns([{
      header: (props) => {
        const { handleCheckAll, checkAll } = props;
        return <FormCheck id="multi-select" type="checkbox" onChange={handleCheckAll} checked={checkAll} />
      },
      body: (user, props) => <FormCheck
        disabled={currentUser.role !== 'SUPER_ADMIN' && user.role === 'SUPER_ADMIN'}
        id={`select-${user.id}`}
        key={`select-${user.id}`}
        type="checkbox"
        checked={checkAll || props.selectedUsers.indexOf(user.id) !== -1}
        onChange={() => props.handleCheckUser(user)}
      />
    }, {
      name: 'username',
      className: 'w-256',
      header: (props) => {
        const { handleFilterKeyUp, handleFilterChange, handleSearch, handleSortChange, filter } = props;
        return <InputGroup>
          <FormControl
            placeholder="Username"
            name="uname"
            aria-label="Username"
            aria-describedby="username"
            value={filter.uname}
            onKeyDown={handleFilterKeyUp}
            onChange={handleFilterChange}
          />
          <InputGroup.Append>
            <Button variant="light" className="btn btn-custom dark" onClick={handleSearch}>
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </InputGroup.Append>
          <InputGroup.Append>
            <Button
              variant="light"
              className="btn btn-custom dark"
              onClick={() => handleSortChange('username')}
            >
              <FontAwesomeIcon
                icon={filter.dir === 'asc' ? faSortAlphaUp : faSortAlphaDown}
                color={filter.sort === 'username' ? 'white' : 'gray'}
              />
            </Button>
          </InputGroup.Append>
        </InputGroup>
      },
      body: (user) => user.username
    }, {
      name: 'role',
      className: 'w-256',
      header: (props) => {
        const { handleFilterChange, handleSearch, handleSortChange, filter } = props;
        return <InputGroup>
          <Select
            placeholder="Role"
            className="flex-item-100 text-dark"
            defaultValue={filter.role}
            isClearable={true}
            options={
              availableRoles?.map((r) => ({
                label: r,
                value: r,
              }))}
            onChange={(option) => handleFilterChange({
              target: {
                value: option?.value,
                name: 'role'
              }
            }, true)}
          />
          <InputGroup.Append className='flex-item-0'>
            <Button variant="light" className="btn btn-custom dark" onClick={handleSearch}>
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </InputGroup.Append>
          <InputGroup.Append className='flex-item-0'>
            <Button
              variant="light"
              className="btn btn-custom dark"
              onClick={() => handleSortChange('role')}
            >
              <FontAwesomeIcon
                icon={filter.dir === 'asc' ? faSortAlphaUp : faSortAlphaDown}
                color={filter.sort === 'role' ? 'white' : 'gray'}
              />
            </Button>
          </InputGroup.Append>
        </InputGroup>
      },
      body: (user) => user.role
    }, {
      name: 'firstName',
      className: 'w-256',
      header: (props) => {
        const { handleFilterKeyUp, handleFilterChange, handleSearch, handleSortChange, filter } = props;
        return <InputGroup className="mb-3">
          <FormControl
            placeholder="First"
            aria-label="First"
            aria-describedby="first"
            name='first'
            value={filter.first}
            onKeyDown={handleFilterKeyUp}
            onChange={handleFilterChange}
          />
          <InputGroup.Append>
            <Button variant="light" className="btn btn-custom dark" onClick={handleSearch}>
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </InputGroup.Append>
          <InputGroup.Append>
            <Button
              variant="light"
              className="btn btn-custom dark"
              onClick={() => handleSortChange('firstName')}
            >
              <FontAwesomeIcon
                icon={filter.dir === 'asc' ? faSortAlphaUp : faSortAlphaDown}
                color={filter.sort === 'firstName' ? 'white' : 'gray'}
              />
            </Button>
          </InputGroup.Append>
        </InputGroup>
      },
      body: (user) => user.firstName
    }, {
      name: 'lastName',
      className: 'w-256',
      header: (props) => {
        const { handleFilterKeyUp, handleFilterChange, handleSearch, handleSortChange, filter } = props;
        return <InputGroup className="mb-3">
          <FormControl
            placeholder="Last"
            aria-label="Last"
            aria-describedby="last"
            name='last'
            value={filter.last}
            onKeyDown={handleFilterKeyUp}
            onChange={handleFilterChange}
          />
          <InputGroup.Append>
            <Button variant="light" className="btn btn-custom dark" onClick={handleSearch}>
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </InputGroup.Append>
          <InputGroup.Append>
            <Button
              variant="light"
              className="btn btn-custom dark"
              onClick={() => handleSortChange('lastName')}
            >
              <FontAwesomeIcon
                icon={filter.dir === 'asc' ? faSortAlphaUp : faSortAlphaDown}
                color={filter.sort === 'lastName' ? 'white' : 'gray'}
              />
            </Button>
          </InputGroup.Append>
        </InputGroup>
      },
      body: (user) => user.lastName
    }, {
      name: 'institution',
      className: 'w-256',
      header: (props) => {
        const { handleFilterChange, handleSearch, filter, institutions } = props;
        return <InputGroup>
          <Select
            placeholder="Institution"
            className="text-dark flex-item-100"
            defaultValue={filter.institutionId}
            isClearable={true}
            options={institutions?.map((l) => ({
              label: l.name,
              value: l.id,
            }))}
            onChange={(option) => handleFilterChange({
              target: {
                value: option?.value,
                name: 'institutionId'
              }
            }, true)}
          />
          <InputGroup.Append className='flex-item-0'>
            <Button variant="light" className="btn btn-custom dark" onClick={handleSearch}>
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </InputGroup.Append>
        </InputGroup>
      },
      body: (user) => user.institution
    }, {
      name: 'organization',
      className: 'min-w-256',
      header: (props) => {
        const { handleFilterChange, handleSearch, filter, orgList } = props;
        return <InputGroup>
          <Select
            placeholder="Organizations"
            className="flex-item-100 text-dark"
            defaultValue={filter.organizationId}
            isClearable={true}
            options={
              orgList?.map((l) => ({
                label: l.name,
                value: l.id,
              }))}

            onChange={(option) => handleFilterChange({
              target: {
                value: option?.value,
                name: 'organizationId'
              }
            }, true)}
          />
          <InputGroup.Append className='flex-item-0'>
            <Button variant="light" className="btn btn-custom dark" onClick={handleSearch}>
              <FontAwesomeIcon icon={faSearch} />
            </Button>
          </InputGroup.Append>
        </InputGroup>
      },
      body: (user, props) => {
        const organizations = user.organizations
          ?.filter(props.filterOrgs)
          ?.map((o) => o.name)
          .join(', ');
        return (
          <OverlayTrigger
            trigger={['hover', 'hover']}
            placement="top"
            overlay={
              <Tooltip id="tooltip-related-question">
                {organizations}
              </Tooltip>
            }
          >
            <div className="org-admin-column">
              {organizations}
            </div>
          </OverlayTrigger>
        )
      }
    }, {
      name: 'lastActive',
      className: 'min-w-160',
      header: (props) => {
        const { filter, handleSortChange } = props;
        return <InputGroup className="d-flex flex-row flex-nowrap">
          <div className='text-center align-baseline  pr-2'>Last Active</div>
          <InputGroup.Append>
            <Button
              variant="light"
              className="btn btn-custom dark"
              onClick={() => handleSortChange('lastLogin')}
            >
              <FontAwesomeIcon
                icon={filter.dir === 'asc' ? faSortAlphaUp : faSortAlphaDown}
                color={filter.sort === 'lastName' ? 'white' : 'gray'}
              />
            </Button>
          </InputGroup.Append>
        </InputGroup>
      },
      body: (user) => user.lastLogin && user.lastLogin !== null
        ? moment(user.lastLogin).format('YYYY MMM DD')
        : '-'
    }, {
      name: 'createdDate',
      className: 'min-w-180',
      header: (props) => {
        const { filter, handleSortChange } = props;
        return <InputGroup className="d-flex flex-row flex-nowrap">
          <div className='text-center align-baseline  pr-2'>{currentUser.role === 'ORGANIZATION_ADMIN' ? 'Date Added' : 'Date Created'}</div>
          <InputGroup.Append>
            <Button
              variant="light"
              className="btn btn-custom dark"
              onClick={() => handleSortChange('createdDate')}
            >
              <FontAwesomeIcon
                icon={filter.dir === 'asc' ? faSortAlphaUp : faSortAlphaDown}
                color={filter.sort === 'createdDate' ? 'white' : 'gray'}
              />
            </Button>
          </InputGroup.Append>
        </InputGroup>
      },
      body: (user, props) => {
        const organizations = user.organizations?.filter(props.filterOrgs);
        return (<>
          {currentUser.role === 'ORGANIZATION_ADMIN' && (
            <>
              {organizations.length > 0 ? moment(organizations[0].addedDate).format('YYYY MMM DD') : '-'}
            </>
          )}
          {currentUser.role !== 'ORGANIZATION_ADMIN' && (
            <>
              {user.createdDate && user.createdDate !== null ? moment(user.createdDate).format('YYYY MMM DD') : '-'}
            </>
          )}
        </>
        )
      }
    }, {
      name: 'status',
      className: 'w-120',
      header: (props) => {
        const { filter, handleSortChange } = props;
        return <InputGroup className="d-flex flex-row flex-nowrap">
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>Waiting_Activation: User has not yet activated their account
                <br />
                Active: User has activated their account
              </Tooltip>
            }
            delay={100}
          >
            <div className="pr-2">Status</div>
          </OverlayTrigger>
          <InputGroup.Append>
            <Button
              variant="light"
              className="btn btn-custom dark"
              onClick={() => handleSortChange('status')}
            >
              <FontAwesomeIcon
                icon={filter.dir === 'asc' ? faSortAlphaUp : faSortAlphaDown}
                color={filter.sort === 'lastName' ? 'white' : 'gray'}
              />
            </Button>
          </InputGroup.Append>
        </InputGroup>
      },
      body: (user) => user.status && user.status !== null ? user.status : '-'
    }, {
      className: 'w-192',
      header: (props) => {
        const { columnsVisible, showAndHideColumn } = props;
        return <InputGroup className="d-flex flex-row flex-nowrap justify-content-end">
          <div className='text-center align-baseline pr-2'>Options</div>
          <OverlayTrigger trigger="click" placement="bottom" overlay={(
            <Popover id="popover-trigger-click" title="Popover bottom">
              <Form.Check
                type="checkbox"
                label={'UserName'}
                id='username'
                checked={columnsVisible['username']}
                onChange={() => showAndHideColumn('username')}
              />
              <FormCheck type="checkbox" onChange={() => showAndHideColumn('role')} checked={columnsVisible.role} label="Role" />
              <FormCheck type="checkbox" onChange={() => showAndHideColumn('firstName')} checked={columnsVisible.firstName} label="First Name" />
              <FormCheck type="checkbox" onChange={() => showAndHideColumn('lastName')} checked={columnsVisible.lastName} label="Last Name" />
              <FormCheck type="checkbox" onChange={() => showAndHideColumn('institution')} checked={columnsVisible.institution} label="Institution" />
              <FormCheck type="checkbox" onChange={() => showAndHideColumn('organization')} checked={columnsVisible.organization} label="Organization" />
              <FormCheck type="checkbox" onChange={() => showAndHideColumn('lastActive')} checked={columnsVisible.lastActive} label="Last Active" />
              {currentUser.role === 'ORGANIZATION_ADMIN' && (
                <FormCheck type="checkbox" onChange={() => showAndHideColumn('createDate')} checked={columnsVisible.createDate} label="Date Added" />
              )}
              {currentUser.role !== 'ORGANIZATION_ADMIN' && (
                <FormCheck type="checkbox" onChange={() => showAndHideColumn('createDate')} checked={columnsVisible.createDate} label="Create Date" />
              )}
              <FormCheck type="checkbox" onChange={() => showAndHideColumn('status')} checked={columnsVisible.status} label="Status" />
            </Popover>)
          }>
            <Button>
              <FontAwesomeIcon icon={faBars} color={'white'} />
            </Button>
          </OverlayTrigger >
        </InputGroup >
      },
      body: (user, props) =>
        <ButtonGroup className='d-flex flex-row flex-nowrap justify-content-end'>
          <Button
            title="Edit User"
            disabled={currentUser.role !== 'SUPER_ADMIN' && user.role === 'SUPER_ADMIN'}
            variant="outline-secondary"
            className="btn btn-custom dark"
            onClick={() => {
              setEditItem(user);
              setShow(true);
            }}
          >
            <FontAwesomeIcon icon={faPencilAlt} />
          </Button>
          <Button
            title="Delete User"
            disabled={
              (currentUser.role !== 'SUPER_ADMIN' && user.role === 'SUPER_ADMIN') ||
              currentUser.userId === user.id
            }
            className="btn btn-custom dark"
            variant="outline-secondary"
            onClick={() => setDeleteItem(user)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </Button>
          {currentUser.role === 'SUPER_ADMIN' && (
            <Button
              title="Remove from all organizations"
              className="btn btn-custom dark"
              variant="outline-secondary"
              onClick={() => {
                setSelectedUsers([user.id]);
                setShowDemote(true);
              }}
              disabled={user.organizations?.filter(props.filterOrgs).length === 0}
            >
              <FontAwesomeIcon icon={faPeopleArrows} />
            </Button>
          )}
        </ButtonGroup>
    }
    ]);
  }, []);

  useEffect(() => {
    loadColumns();
  }, []);

  return (
    <Container fluid className="px-5">
      <Row className="mb-2">
        <ButtonGroup>
          <button
            title="Create User"
            className="btn btn-custom dark"
            type="button"
            onClick={handleCreateUser}
          >
            <FontAwesomeIcon icon={faUserPlus} />
          </button>
          <UserBatch handleExportUsersTemplate={handleExportUsersTemplate} />
          <button
            title={
              currentUser.role === 'ORGANIZATION_ADMIN'
                ? 'Remove all selected from all organizations'
                : 'Delete Selected Users'
            }
            className="btn btn-custom dark"
            type="button"
            disabled={selectedUsers.length === 0}
            onClick={() => setShowMultiDelete(true)}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
          <button
            title="Clear Filters"
            className="btn btn-custom dark"
            type="button"
            onClick={handleClearFilters}
          >
            <FontAwesomeIcon icon={faTimesCircle} />
          </button>
          <button
            title="Export Users"
            className="btn btn-custom dark"
            disabled={isProcessingExport}
            type="button"
            onClick={handleExportUsers}
          >
            <FontAwesomeIcon icon={faUsersCog} />
          </button>
          <button
            title="Refresh Users List"
            className="btn btn-custom dark"
            type="button"
            onClick={() => retrieveUsers()}
          >
            <FontAwesomeIcon icon={faRedo} />
          </button>
          {currentUser.role === 'SUPER_ADMIN' && (
            <button
              title="Remove from all organizations"
              className="btn btn-custom dark"
              variant="outline-secondary"
              type="button"
              disabled={selectedUsers.length === 0}
              onClick={() => setShowDemote(true)}
            >
              <FontAwesomeIcon icon={faPeopleArrows} />
            </button>
          )}
        </ButtonGroup>
      </Row>
      <Row>
        <Table responsive="xl" striped bordered hover className="w-100">
          <thead className="custom-thead">
            <tr>
              {columns.map((column, index) => (
                columnsVisible[column.name] !== false && <th key={`header-${index}`} className={`text-center align-baseline ${column.className || ''}`}>
                  {column.header({
                    handleCheckAll,
                    checkAll,
                    handleFilterKeyUp,
                    handleFilterChange,
                    handleSearch,
                    handleSortChange,
                    filter,
                    columnsVisible,
                    showAndHideColumn,
                    institutions,
                    orgList,
                    filterOrgs
                  })}
                </th>
              ))}
            </tr>
          </thead>
          {loading && orgLoading && (
            <tbody>
              <tr className="text-center p-5 vh-100">
                <td>
                  <Spinner animation="border" />
                </td>
              </tr>
            </tbody>
          )}
          {!loading && !orgLoading && (
            <tbody>
              {userList.map((user) => (
                <tr key={user.id}>
                  {columns.map((column, index) => (
                    columnsVisible[column.name] !== false && <td key={`row-${index}`}>
                      {column.body(user, {
                        selectedUsers,
                        handleCheckUser,
                        filterOrgs
                      })}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </Table>
      </Row>
      <Row>
        <Col xs={1}>
          <FormControl as="select" onChange={handlePageSizeChange}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </FormControl>
        </Col>
        <Col>
          <Pagination
            currentPage={filter.page}
            onPageChange={handleChangePage}
            pageSize={filter.size}
            totalItems={totalSize}
          />
        </Col>
      </Row>
      <DeleteUserModal
        deleteItem={deleteItem}
        handleClose={handleClose}
        currentUser={currentUser}
        handleDelete={handleDelete}
        filterOrgs={filterOrgs}
      />
      <MultiDeleteUserModal
        showMultiDelete={showMultiDelete}
        handleDeleteMultiple={handleDeleteMultiple}
        usersSelectedHaveMoreOrgs={usersSelectedHaveMoreOrgs}
        currentUser={currentUser}
        handleClose={handleClose}
      />
      <RemoveFromOrganizationModal showDemote={showDemote} handleClose={handleClose} handleDemoteUser={handleDemoteUser} />
      <UserForm
        show={show}
        handleClose={handleClose}
        handleSave={handleSave}
        saveError={saveError}
        orgList={orgList}
        editItem={editItem}
      />
    </Container>
  );
};

export default UserList;
